import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  getIn,
  setIn,
} from 'formik';

import {
  arePersonFieldValuesEqual,
  findPersonFormField,
  getPersonFieldValueSyncRules,
  getPersonFieldValueSyncUpdate,
} from 'utils/personFieldValueSync';

const getFullFieldName = (namePrefix, fieldName) =>
  namePrefix ? `${namePrefix}${fieldName}` : fieldName;

/**
 * Synchronizes configured Person form fields after a user changes a source
 * field. The target is written through Formik, so disabled controls can still
 * receive the synchronized value.
 *
 * The first value observed for each source is treated as its initial value and
 * is never copied. This preserves values loaded from the API in Edit. When a
 * select target has not loaded its options yet, the rule remains pending and
 * is retried on later form renders.
 */
function usePersonFieldValueSync({
  company,
  fields = [],
  formikRef,
  namePrefix = '',
  resetKey,
  roleTemplateId,
  templateId,
}) {
  const previousSourceValues = useRef({});
  const pendingRuleKeys = useRef(new Set());
  const isInitialRun = useRef(true);
  const validationTimerRef = useRef(null);

  const rules = useMemo(
    () => getPersonFieldValueSyncRules({
      company,
      roleTemplateId,
      template: templateId,
    }),
    [company, roleTemplateId, templateId],
  );

  const resetPersonFieldValueSync = useCallback(() => {
    if (validationTimerRef.current !== null) {
      clearTimeout(validationTimerRef.current);
      validationTimerRef.current = null;
    }

    previousSourceValues.current = {};
    pendingRuleKeys.current = new Set();
    isInitialRun.current = true;
  }, []);

  useEffect(() => () => {
    if (validationTimerRef.current !== null) {
      clearTimeout(validationTimerRef.current);
      validationTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    resetPersonFieldValueSync();
  }, [resetKey, resetPersonFieldValueSync]);

  const onFormChange = useCallback(
    (values, formikProps) => {
      const shouldWriteValues = !isInitialRun.current;
      const setFieldValue = formikRef?.current?.setFieldValue;
      const validateForm = formikProps?.validateForm;
      const canBatchValidation = typeof validateForm === 'function';
      const pendingSourceSnapshots = {};
      let needsRevalidation = false;
      let nextValues = values;

      rules.forEach((rule, index) => {
        const sourceField = findPersonFormField({
          fields,
          fieldName: rule.sourceField,
          namePrefix,
        });

        // A field can be absent from a template or from the active tab. In
        // either case the configured rule is intentionally a no-op.
        if (!sourceField) return;

        const ruleKey = `${index}:${rule.sourceField}:${rule.targetField}`;
        const sourceName = getFullFieldName(namePrefix, rule.sourceField);
        const sourceValue = getIn(values, sourceName);
        const hasPreviousValue = Object.prototype.hasOwnProperty.call(
          previousSourceValues.current,
          ruleKey,
        );
        const sourceChanged = !hasPreviousValue
          || !arePersonFieldValuesEqual(
            sourceValue,
            previousSourceValues.current[ruleKey],
          );

        pendingSourceSnapshots[ruleKey] = sourceValue;

        const shouldRetry = pendingRuleKeys.current.has(ruleKey);
        // The first value observed for a source establishes the baseline.
        // This also avoids syncing an API value merely because its tab was
        // opened later than the rest of the form.
        if (
          !shouldWriteValues
          || (!shouldRetry && (!hasPreviousValue || !sourceChanged))
        ) {
          return;
        }

        const update = getPersonFieldValueSyncUpdate({
          fields,
          namePrefix,
          rule,
          values: nextValues,
        });

        if (!update || typeof setFieldValue !== 'function') {
          pendingRuleKeys.current.add(ruleKey);
          return;
        }

        nextValues = setIn(nextValues, update.name, update.value);
        setFieldValue(
          update.name,
          update.value,
          canBatchValidation ? false : undefined,
        );
        pendingRuleKeys.current.delete(ruleKey);
        needsRevalidation = true;
      });

      Object.assign(previousSourceValues.current, pendingSourceSnapshots);
      isInitialRun.current = false;

      if (needsRevalidation && canBatchValidation) {
        if (validationTimerRef.current !== null) {
          clearTimeout(validationTimerRef.current);
        }

        validationTimerRef.current = setTimeout(() => {
          validationTimerRef.current = null;
          validateForm(nextValues);
        }, 0);
      }
    },
    [fields, formikRef, namePrefix, rules],
  );

  return {
    onFormChange,
    resetPersonFieldValueSync,
  };
}

export default usePersonFieldValueSync;
